import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  Calendar, Clock, Monitor, Ticket, ArrowRight, Check, X,
  Users, Compass, HeartHandshake, CheckCircle2, Gauge, Volume2,
  RefreshCw, Wallet, Eye, MessageCircle, Home, Package, Ban, Megaphone,
} from "lucide-react";
import { submitWorkshopLead, trackWorkshopEvent } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Martedì 27 ottobre 2026, ore 20:30 (Europe/Rome, CET = UTC+1: l'ora legale finisce il 25/10)
const TARGET = new Date("2026-10-27T20:30:00+01:00").getTime();
const DATA_LUNGA = "martedì 27 ottobre, alle 20:30";
// Nome evento inviato a GHL (compare nelle notifiche di Pietro) e nel Pixel
export const EVENTO = "Network marketing online · 27 ottobre";
// Video di Massimiliano (60"): quando arriva va in /public/workshop/intro.mp4 e qui si mette "/workshop/intro.mp4"
const HERO_VIDEO = "/workshop/intro.mp4";

const landingFonte = () => (typeof window !== "undefined" && window.location.search) || "landing";
const trackOnce = (evento) => {
  const key = `ws_${evento}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch { /* storage bloccato: traccia comunque */ }
  trackWorkshopEvent({ evento, fonte: landingFonte(), path: window.location.pathname });
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

function useDocTitle(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => { document.title = prev; };
  }, [title]);
}

function useCountdown(target) {
  const [left, setLeft] = useState(() => Math.max(0, target - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const s = Math.floor(left / 1000);
  return {
    giorni: Math.floor(s / 86400),
    ore: Math.floor((s % 86400) / 3600),
    minuti: Math.floor((s % 3600) / 60),
    secondi: s % 60,
    finito: left <= 0,
  };
}

const Kicker = ({ num, children }) => (
  <div className="flex items-center gap-3 mb-5">
    {num ? (
      <span className="font-display font-black text-[#F59E0B] text-sm tracking-[0.2em]">#{num}</span>
    ) : (
      <span className="h-[3px] w-9 bg-[#F59E0B]" />
    )}
    <span className="text-[0.7rem] sm:text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
      {children}
    </span>
  </div>
);

const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 bg-[#EF4444] text-white text-[0.7rem] sm:text-sm font-black uppercase tracking-[0.15em] px-3 py-1 rounded-full" data-testid="live-badge">
    <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> Live
  </span>
);

const CountBox = ({ value, label, testid }) => (
  <div className="flex flex-col items-center" data-testid={testid}>
    <div className="w-[68px] sm:w-[92px] py-3 sm:py-4 bg-[#141F36] border border-[#3A4763] rounded-md">
      <span className="block text-3xl sm:text-5xl font-display font-black tabular-nums leading-none text-white text-center">
        {String(value).padStart(2, "0")}
      </span>
    </div>
    <span className="mt-2 text-[0.6rem] sm:text-xs uppercase tracking-[0.2em] text-[#94A3B8]">{label}</span>
  </div>
);

const Countdown = ({ compact = false, prefix = "" }) => {
  const c = useCountdown(TARGET);
  if (c.finito) {
    return (
      <p className="text-[#34D399] font-semibold" data-testid={`${prefix}countdown-live`}>
        Il workshop è iniziato — se vi siete prenotati, controllate email e telefono.
      </p>
    );
  }
  return (
    <div className={`flex items-start gap-3 sm:gap-4 ${compact ? "justify-start" : "justify-center"}`} data-testid={`${prefix}countdown`}>
      <CountBox value={c.giorni} label="Giorni" testid={`${prefix}countdown-giorni`} />
      <CountBox value={c.ore} label="Ore" testid={`${prefix}countdown-ore`} />
      <CountBox value={c.minuti} label="Minuti" testid={`${prefix}countdown-minuti`} />
      <CountBox value={c.secondi} label="Secondi" testid={`${prefix}countdown-secondi`} />
    </div>
  );
};

const MetaChip = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 px-3.5 py-2 bg-[#141F36] border border-[#3A4763] rounded-full text-sm text-[#E2E8F0]">
    <Icon size={15} className="text-[#F59E0B]" />
    <span className="font-medium">{children}</span>
  </div>
);

const inputCls = "w-full bg-[#0B1120] border border-[#3A4763] rounded-md px-4 py-3 text-white placeholder:text-[#475569] outline-none focus:border-[#F59E0B] transition-colors";

const ETA_FASCE = ["18-25", "26-35", "36-45", "46-55", "oltre 55"];

const LeadFormFields = ({ form, set, submit, loading, prefix = "", compact = false }) => (
  <form onSubmit={submit} onFocusCapture={() => trackOnce("form_click")} className={compact ? "space-y-3" : "space-y-4"} data-testid={`${prefix}lead-form`}>
    <div>
      {!compact && <label className="block text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1.5">Nome e cognome</label>}
      <input data-testid={`${prefix}input-nome`} type="text" value={form.nome} onChange={set("nome")}
        placeholder="Il tuo nome e cognome" autoComplete="name" className={inputCls} />
    </div>
    <div>
      {!compact && <label className="block text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1.5">Email</label>}
      <input data-testid={`${prefix}input-email`} type="email" value={form.email} onChange={set("email")}
        placeholder="La tua email migliore" autoComplete="email" inputMode="email" className={inputCls} />
    </div>
    <div className={compact ? "grid grid-cols-[1fr_118px] gap-3" : "grid sm:grid-cols-[1fr_150px] gap-4"}>
      <div>
        {!compact && <label className="block text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1.5">Telefono / WhatsApp</label>}
        <input data-testid={`${prefix}input-telefono`} type="tel" value={form.telefono} onChange={set("telefono")}
          placeholder={compact ? "Cellulare (WhatsApp)" : "Numero di cellulare"} autoComplete="tel" inputMode="tel" className={inputCls} />
      </div>
      <div>
        {!compact && <label className="block text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1.5">Età</label>}
        <select data-testid={`${prefix}input-eta`} value={form.eta} onChange={set("eta")}
          className={`${inputCls} appearance-none ${form.eta ? "text-white" : "text-[#475569]"}`}>
          <option value="" disabled>Età</option>
          {ETA_FASCE.map((f) => <option key={f} value={f} className="text-white bg-[#0B1120]">{f}</option>)}
        </select>
      </div>
    </div>
    <label className="flex items-start gap-3 text-sm text-[#94A3B8] cursor-pointer">
      <input data-testid={`${prefix}input-consenso`} type="checkbox" checked={form.consenso} onChange={set("consenso")}
        className="mt-1 w-4 h-4 accent-[#F59E0B] shrink-0" />
      <span className={compact ? "text-xs leading-snug" : ""}>
        Acconsentite al trattamento dei vostri dati per essere ricontattati riguardo al workshop, come da{" "}
        <a href={`${BACKEND_URL}/api/privacy`} target="_blank" rel="noreferrer" className="text-[#F59E0B] underline">informativa privacy</a>.
      </span>
    </label>
    <button data-testid={`${prefix}form-submit`} type="submit" disabled={loading}
      className="w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] disabled:opacity-60 text-[#0B1120] font-bold text-base px-6 py-4 rounded-full transition-colors">
      {loading ? "Invio in corso…" : (<>Prenotate il vostro posto gratis <ArrowRight size={18} /></>)}
    </button>
    <p className="text-center text-xs text-[#64748B] text-pretty">
      {compact ? "Gratis · Zoom · il link arriva via email e WhatsApp." : "Niente spam: ricevete solo l'invito, il link Zoom e qualche promemoria prima della serata."}
    </p>
  </form>
);

const HeroPromise = () => (
  <p className="text-base sm:text-lg text-white leading-relaxed max-w-xl" data-testid="hero-promise">
    Non vi prometto la vita in barca a Dubai.
    <br />
    Vi mostrerò come arrivare a fine mese con
    <br />
    <span className="text-[#F59E0B] font-semibold">un respiro in più</span>.
  </p>
);

const HeroChips = () => (
  <div className="mt-6 flex flex-wrap gap-2.5" data-testid="hero-chips">
    <MetaChip icon={Calendar}>Mar 27 Ott 2026</MetaChip>
    <MetaChip icon={Clock}>Ore 20:30</MetaChip>
    <MetaChip icon={Monitor}>Online su Zoom</MetaChip>
    <MetaChip icon={Ticket}>Gratis · posti limitati</MetaChip>
  </div>
);

// Video verticale 9:16 (come i reel): la foto fa da segnaposto finché non arriva il file
const HeroMedia = () => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const unmute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.currentTime = 0;
    v.play();
    setMuted(false);
  };
  if (!HERO_VIDEO) {
    return (
      <div className="relative">
        <img src="/workshop/hero.webp" alt="Massimiliano Pulla sul palco"
          className="w-full aspect-[9/16] object-cover object-[62%_center]" data-testid="hero-image" />
        {process.env.NODE_ENV !== "production" && (
          <span className="absolute bottom-3 left-3 right-3 inline-flex items-center justify-center gap-1.5 bg-[#0B1120]/85 text-[#F59E0B] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#F59E0B]/40" data-testid="hero-video-placeholder">
            <Volume2 size={13} /> Qui va il video (60", verticale)
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="relative">
      <video ref={ref} src={HERO_VIDEO} poster="/workshop/intro-poster.jpg" autoPlay muted loop={muted} playsInline controls={!muted} preload="metadata"
        className="w-full aspect-[9/16] object-cover bg-black" data-testid="hero-video" />
      {muted && (
        <button type="button" onClick={unmute} data-testid="hero-video-unmute"
          className="absolute inset-0 flex items-end justify-center pb-5 bg-black/15 hover:bg-black/25 transition-colors">
          <span className="inline-flex items-center gap-2 bg-[#F59E0B] text-[#0B1120] font-bold px-5 py-3 rounded-full shadow-lg">
            <Volume2 size={18} /> Attiva l'audio
          </span>
        </button>
      )}
    </div>
  );
};

const Card = ({ children, className = "", testid, delay = 0 }) => (
  <motion.div {...fadeUp} transition={{ duration: 0.45, delay }}
    className={`p-6 bg-[#141F36] border border-[#1E2A44] rounded-md ${className}`} data-testid={testid}>
    {children}
  </motion.div>
);

export default function WorkshopLanding() {
  useDocTitle("Network marketing online — Workshop gratuito | martedì 27 ottobre, ore 20:30");
  const navigate = useNavigate();
  const formRef = useRef(null);
  const heroFormRef = useRef(null);
  const deskFormRef = useRef(null);
  const bottomFormRef = useRef(null);
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", eta: "", consenso: false });
  const [loading, setLoading] = useState(false);
  const [formsVisible, setFormsVisible] = useState({ hero: true, desk: true, bottom: false });

  useEffect(() => { trackOnce("view"); }, []);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const key = en.target === heroFormRef.current ? "hero" : en.target === deskFormRef.current ? "desk" : "bottom";
        setFormsVisible((v) => ({ ...v, [key]: en.isIntersecting }));
      });
    }, { threshold: 0.15 });
    [heroFormRef.current, deskFormRef.current, bottomFormRef.current].forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const showStickyBar = !formsVisible.hero && !formsVisible.desk && !formsVisible.bottom;
  const scrollToBottomForm = useCallback(() => {
    bottomFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.telefono.trim()) {
      toast.error("Compilate nome, email e telefono.");
      return;
    }
    if (!form.eta) {
      toast.error("Indicate la vostra età.");
      return;
    }
    if (!form.consenso) {
      toast.error("Serve il consenso al trattamento dei dati.");
      return;
    }
    setLoading(true);
    try {
      const nome = form.nome.trim(), email = form.email.trim(), telefono = form.telefono.trim();
      await submitWorkshopLead({ nome, email, telefono, eta: form.eta, consenso: true, evento: EVENTO, fonte: landingFonte() });
      navigate("/workshop/grazie", { state: { nome, email, telefono } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Qualcosa è andato storto. Riprovate.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "consenso" ? e.target.checked : e.target.value }));

  return (
    <div className="min-h-screen bg-[#0B1120] text-white font-body antialiased overflow-x-hidden pb-20">
      <Toaster theme="dark" position="top-center" richColors />

      {/* NAV */}
      <header className="sticky top-0 z-40 border-b border-[#1E2A44] bg-[#0B1120]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="wordmark">
            <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
            <span className="font-display font-extrabold tracking-[0.16em] text-sm sm:text-base uppercase">
              Massimiliano Pulla
            </span>
          </div>
          <button
            data-testid="nav-cta"
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] text-[#0B1120] font-bold text-sm px-4 sm:px-5 py-2 sm:py-2.5 rounded-full transition-colors"
          >
            Prenota il posto <ArrowRight size={16} />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-12 grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-start">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="h-[3px] w-9 bg-[#F59E0B]" />
              <span className="text-[0.7rem] sm:text-xs font-semibold uppercase tracking-[0.25em] text-[#94A3B8]">
                Workshop gratuito · Network marketing online
              </span>
              <LiveBadge />
            </div>

            <p className="text-[#F59E0B] font-bold text-lg sm:text-xl mb-5" data-testid="hero-datetime">
              Martedì 27 ottobre · ore 20:30
            </p>

            <h1 className="font-display font-black leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance" data-testid="hero-title">
              Faccio network marketing da 6 anni.<br />
              <span className="text-[#F59E0B]">E vi sconsiglio di iniziarlo</span> come l'avete visto fare.
            </h1>

            <p className="mt-6 text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-xl text-pretty" data-testid="hero-sub">
              Martedì 27 vi mostro l'altro modo, quello <span className="text-white font-semibold">online</span>: non andate voi dalle persone,{" "}
              <span className="text-[#F59E0B] font-semibold">arrivano loro</span>. Io e mia moglie ne abbiamo fatto il nostro lavoro.
              Quasi tutti però cominciano la sera, come seconda entrata: vi faccio vedere come si parte, passo per passo. Gratis, su Zoom.
            </p>

            <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start">
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative mx-auto lg:mx-0 w-[240px] sm:w-[260px] lg:w-[280px] shrink-0"
                data-testid="hero-media"
              >
                <div className="absolute -inset-2 bg-[#F59E0B]/10 rounded-2xl blur-2xl" />
                <div className="relative rounded-xl overflow-hidden border border-[#3A4763] bg-[#141F36]">
                  <HeroMedia />
                </div>
              </motion.div>
              <div className="hidden lg:block pt-2">
                <HeroPromise />
                <HeroChips />
              </div>
            </div>

            <div ref={heroFormRef} className="mt-6 lg:hidden bg-[#141F36] border border-[#F59E0B]/50 rounded-xl p-5" data-testid="hero-form-card">
              <p className="font-display font-bold text-white text-lg leading-tight">Prenotate il vostro posto gratis</p>
              <p className="text-xs text-[#94A3B8] mt-1 mb-4">Un minuto. Poi ci vediamo martedì 27 alle 20:30.</p>
              <LeadFormFields form={form} set={set} submit={submit} loading={loading} prefix="hero-" compact />
            </div>

            <div className="lg:hidden mt-6">
              <HeroPromise />
              <HeroChips />
            </div>
          </motion.div>

          <motion.div
            ref={deskFormRef}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden lg:block lg:sticky lg:top-24 bg-[#141F36] border border-[#F59E0B]/50 rounded-xl p-6 shadow-xl shadow-black/30"
            data-testid="hero-form-desktop"
          >
            <p className="font-display font-bold text-white text-xl leading-tight">Prenotate il vostro posto gratis</p>
            <p className="text-sm text-[#94A3B8] mt-1 mb-5">Un minuto. Poi ci vediamo martedì 27 alle 20:30.</p>
            <LeadFormFields form={form} set={set} submit={submit} loading={loading} prefix="desk-" compact />
            <p className="mt-4 text-center text-xs text-[#64748B]">Nessun costo. Solo il vostro tempo e la voglia di ascoltare.</p>
          </motion.div>
        </div>
      </section>

      {/* #01 DOVE SIETE ADESSO */}
      <section className="border-y border-[#1E2A44] bg-[#0D1526]" data-testid="sez-dove-siete">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <motion.div {...fadeUp}>
            <Kicker num="01">Dove siete adesso</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl text-balance">
              Lavorate, vi impegnate. Eppure a fine mese resta sempre poco.
            </h2>
            <p className="mt-6 text-white text-base sm:text-lg max-w-2xl leading-relaxed text-pretty border-l-2 border-[#F59E0B] pl-4" data-testid="scena-cucina">
              È venerdì sera. Tornate alle otto: uno scalda la cena, l'altro guarda il conto sul telefono.
              «Com'è andata questo mese?» «Come il mese scorso.» E la serata finisce lì.
            </p>
            <p className="mt-5 text-[#94A3B8] text-base sm:text-lg max-w-2xl leading-relaxed text-pretty">
              Non è pigrizia. Non è sfortuna.
              <br />
              È così per milioni di coppie in Italia — e non è colpa vostra.
            </p>
          </motion.div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              "Un solo stipendio non basta più. E spesso non bastano nemmeno due.",
              "Il tempo insieme finisce in fondo alla lista, dopo il lavoro e le scadenze.",
              "Un figlio, una casa, una vacanza vera: rimandati a «quando le cose andranno meglio».",
              "E intanto gli anni passano, mentre aspettate un momento che non arriva.",
            ].map((t, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="flex gap-3 p-5 bg-[#141F36] border border-[#1E2A44] rounded-md"
                data-testid={`pain-${i}`}
              >
                <X size={20} className="text-[#F87171] shrink-0 mt-0.5" />
                <p className="text-[#E2E8F0] leading-relaxed text-pretty">{t}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-10">
            <p className="font-display font-bold text-xl sm:text-2xl text-[#F59E0B]">Noi eravamo esattamente lì.</p>
            <p className="mt-3 text-[#94A3B8] text-base sm:text-lg max-w-2xl leading-relaxed text-pretty" data-testid="nome-problema">
              Il problema ha un nome, e non è il vostro: è un sistema in cui lo stipendio resta fermo e tutto il resto
              sale. L'unica via d'uscita che vi hanno insegnato è lavorare di più. Noi ne abbiamo trovata un'altra.
            </p>
          </motion.div>
        </div>
      </section>

      {/* COUNTDOWN BAND */}
      <section className="bg-[#0B1120]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-16 text-center">
          <motion.div {...fadeUp} className="flex flex-col items-center">
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.25em] text-[#94A3B8] mb-3">
              Manca poco
            </span>
            <h3 className="font-display font-black text-2xl sm:text-3xl mb-8 text-[#F59E0B]">
              Martedì 27 ottobre · ore 20:30
            </h3>
            <Countdown />
          </motion.div>
        </div>
      </section>

      {/* #02 DICIAMOLO SUBITO */}
      <section className="border-y border-[#F59E0B]/30 bg-[#0D1526]" data-testid="sez-trasparenza">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <motion.div {...fadeUp}>
            <Kicker num="02">Le carte in tavola</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl text-balance" data-testid="trasparenza-title">
              Diciamolo subito: è <span className="text-[#F59E0B]">network marketing</span>.
            </h2>
            <p className="mt-5 text-[#94A3B8] text-base sm:text-lg max-w-2xl leading-relaxed text-pretty">
              Lo scrivo qui, prima che vi iscriviate, perché voglio che entriate sapendo cosa troverete.
            </p>
          </motion.div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { icon: Package, t: "Cos'è", d: "Si usano e si fanno conoscere i prodotti di un'azienda, e si costruisce una squadra di persone che fanno lo stesso. Da casa, online." },
              { icon: Ban, t: "Cosa non è", d: "Non è uno stipendio e non è un lavoro dipendente. Non ci sono guadagni garantiti: è un'attività vostra, e rende in base a quanto ci lavorate." },
              { icon: Megaphone, t: "Perché lo scrivo qui", d: "Perché preferisco che lo sappiate prima di prenotare. Chi viene martedì sa già di cosa parliamo, e la serata va dritta al punto: come funziona, e come si parte." },
            ].map((c, i) => (
              <Card key={i} delay={i * 0.06} testid={`trasparenza-${i}`}>
                <div className="w-10 h-10 rounded-md bg-[#F59E0B]/12 flex items-center justify-center mb-4">
                  <c.icon size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{c.t}</h3>
                <p className="text-[#94A3B8] leading-relaxed text-pretty">{c.d}</p>
              </Card>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-8 p-6 sm:p-8 bg-[#141F36] border border-[#F59E0B]/40 rounded-md max-w-3xl" data-testid="scetticismo-box">
            <h3 className="font-display font-bold text-xl sm:text-2xl text-white text-balance">
              Se «network marketing» vi fa alzare un sopracciglio, avete ragione.
            </h3>
            <p className="mt-4 text-[#94A3B8] text-base sm:text-lg leading-relaxed text-pretty">
              Per anni è stato fatto male: messaggi a chi non li aveva chiesti, promesse che non stavano in piedi.
              Oggi basta nominarlo e la gente si irrigidisce. Ma non è lo strumento a essere sbagliato: è come è stato usato.
            </p>
            <p className="mt-4 text-white text-base sm:text-lg leading-relaxed text-pretty">
              Martedì vi faccio vedere come lo facciamo noi, dove nessuno insegue nessuno. Poi giudicate voi.
            </p>
          </motion.div>

          <motion.p {...fadeUp} className="mt-8 text-sm text-[#64748B] max-w-2xl text-pretty" data-testid="trasparenza-nota">
            Niente promesse di guadagno, né qui né in serata: i risultati dipendono dal lavoro che ci mettete e non sono garantiti.
          </motion.p>
        </div>
      </section>

      {/* #03 STORIA */}
      <section className="bg-[#0B1120]" data-testid="sez-storia">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="rounded-xl overflow-hidden border border-[#3A4763]">
              <img
                src="/workshop/couple.jpg"
                alt="Massimiliano e sua moglie"
                className="w-full h-[300px] sm:h-[420px] object-cover"
                data-testid="storia-image"
              />
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="order-1 lg:order-2">
            <Kicker num="03">La nostra storia</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight text-balance">
              Da 12 ore al giorno a una vita che abbiamo scelto noi.
            </h2>
            <div className="mt-6 space-y-4 text-[#94A3B8] text-base sm:text-lg leading-relaxed text-pretty">
              <p>
                Per anni abbiamo lavorato 12 ore al giorno, dal lunedì al sabato.
                <br />
                Vedevamo le nostre famiglie due o tre volte l'anno.
                <br />
                E ogni volta che parlavamo di avere un figlio, la risposta era la stessa: «Come facciamo? Non ci siamo mai».
              </p>
              <p>
                Guadagnavamo bene, ma non abbastanza da sentirci liberi.
                <br />
                Finché non abbiamo trovato una via d'uscita nel network marketing online — e abbiamo deciso di cambiare
                tutto, senza cambiare chi siamo.
              </p>
              <p className="text-white">
                Oggi nostro figlio lo vediamo crescere ogni giorno.
                <br />
                Ci siamo sposati, abbiamo costruito la nostra casa, e il tempo — quello che prima non avevamo mai — adesso è nostro.
              </p>
            </div>
            <p className="mt-7 border-l-2 border-[#F59E0B] pl-4 font-display font-bold text-lg sm:text-xl text-white text-balance">
              Non vi prometto la nostra vita.
              <br />
              <span className="text-[#F59E0B]">Vi mostrerò la strada per arrivare a fine mese con più respiro.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* GALLERIA "IL DOPO" */}
      <section className="border-y border-[#1E2A44] bg-[#0D1526]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-16">
          <motion.div {...fadeUp}><Kicker>Il dopo</Kicker></motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { src: "/workshop/fam-casa.jpg", alt: "In famiglia a casa" },
              { src: "/workshop/fam-piazza.webp", alt: "In famiglia in piazza" },
              { src: "/workshop/fam-festa.webp", alt: "La festa dei 3 anni" },
              { src: "/workshop/fam-giardino.jpg", alt: "In famiglia in giardino" },
            ].map((img, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-md overflow-hidden border border-[#1E2A44] aspect-[3/4]"
                data-testid={`gallery-${i}`}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* #04 AL CONTRARIO */}
      <section className="bg-[#0B1120]" data-testid="sez-al-contrario">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <motion.div {...fadeUp}>
            <Kicker num="04">Come funziona online</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl text-balance" data-testid="al-contrario-title">
              Una cosa semplice: non cercate voi le persone. <span className="text-[#F59E0B]">Arrivano loro.</span>
            </h2>
            <p className="mt-5 text-[#94A3B8] text-base sm:text-lg max-w-2xl leading-relaxed text-pretty">
              Il network marketing come l'avete visto fare parte sempre da chi conoscete già. E quando li avete sentiti tutti, si ferma.
              Online si fa al contrario, in tre passi.
            </p>
          </motion.div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { icon: Eye, t: "Vi fate conoscere prima", d: "Online raccontate quello che vivete: il fine mese, i figli, il lavoro. Chi vi somiglia si ferma e vi segue." },
              { icon: MessageCircle, t: "Vi scrivono loro", d: "Quando qualcuno vi scrive, vi segue da settimane: sa già chi siete e cosa fate. Non dovete convincere nessuno." },
              { icon: Home, t: "Si lavora da casa, anche in due", d: "La sera, nel tempo che avete. Con un metodo preciso e con me accanto nei primi mesi." },
            ].map((c, i) => (
              <Card key={i} delay={i * 0.06} testid={`passo-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-display font-black text-[#F59E0B] text-2xl">{i + 1}</span>
                  <c.icon size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{c.t}</h3>
                <p className="text-[#94A3B8] leading-relaxed text-pretty">{c.d}</p>
              </Card>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-8 p-6 sm:p-7 bg-[#141F36] border border-[#F59E0B]/40 rounded-md max-w-3xl" data-testid="autorevolezza-box">
            <p className="text-white text-base sm:text-lg leading-relaxed text-pretty">
              Questo, per me, è l'<span className="text-[#F59E0B] font-semibold">autorevolezza online</span>: che chi vi scrive si fidi già.
              Non sono un social media manager e non vi insegno a diventare famosi. Vi faccio vedere come farvi trovare da chi vi somiglia.
            </p>
          </motion.div>
        </div>
      </section>

      {/* #05 COSA VEDREMO */}
      <section className="border-y border-[#1E2A44] bg-[#0D1526]" data-testid="sez-vedremo">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <motion.div {...fadeUp}>
            <Kicker num="05">Cosa vedremo insieme</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl text-balance">
              Una serata concreta. Senza fumo, senza promesse gonfiate.
            </h2>
          </motion.div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Compass, t: "Perché il network marketing «classico» si ferma", d: "Cosa succede quando le persone intorno a voi le avete già sentite tutte. E perché non è colpa di chi ci ha provato." },
              { icon: RefreshCw, t: "Come si fa online, ogni giorno", d: "Cosa facciamo in pratica perché le persone arrivino da noi. Dall'inizio, senza dare niente per scontato." },
              { icon: HeartHandshake, t: "La nostra storia, per intero", d: "Da dove siamo partiti, cosa abbiamo fatto davvero e i risultati che abbiamo ottenuto. Con i fatti, non con le frasi." },
              { icon: Wallet, t: "Cosa serve davvero per iniziare", d: "Quanto tempo ci vuole, come si parte la sera, e cosa aspettarsi nei primi mesi. Detto chiaro, senza cifre gonfiate." },
            ].map((c, i) => (
              <Card key={i} delay={i * 0.06} testid={`vedremo-${i}`} className="hover:border-[#3A4763] transition-colors">
                <div className="w-10 h-10 rounded-md bg-[#F59E0B]/12 flex items-center justify-center mb-4">
                  <c.icon size={20} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{c.t}</h3>
                <p className="text-[#94A3B8] leading-relaxed text-pretty">{c.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* #06 PER CHI È */}
      <section className="bg-[#0B1120]" data-testid="sez-per-chi">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20">
          <motion.div {...fadeUp}><Kicker num="06">Per chi è</Kicker></motion.div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-16 sm:pb-20 grid md:grid-cols-2 gap-4 sm:gap-6">
          <motion.div {...fadeUp} className="p-7 bg-[#141F36] border border-[#1E2A44] rounded-md" data-testid="per-chi-si">
            <div className="flex items-center gap-2 mb-5">
              <CheckCircle2 size={20} className="text-[#34D399]" />
              <h3 className="font-display font-bold text-xl">È per voi se…</h3>
            </div>
            <ul className="space-y-3">
              {[
                "Non avete mai fatto network marketing e cercate una seconda entrata seria, da costruire anche in due, nel tempo che avete.",
                "Siete già nel network marketing ma siete fermi: le persone intorno a voi le avete già sentite tutte, e di nuove non ne arrivano.",
                "Lavorate, vi impegnate, ma sentite che «così non va». E volete un'alternativa concreta, non l'ennesima promessa.",
              ].map((t, i) => (
                <li key={i} className="flex gap-3 text-[#E2E8F0] leading-relaxed text-pretty">
                  <Check size={18} className="text-[#34D399] shrink-0 mt-1" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp} className="p-7 bg-[#141F36] border border-[#1E2A44] rounded-md" data-testid="per-chi-no">
            <div className="flex items-center gap-2 mb-5">
              <X size={20} className="text-[#F87171]" />
              <h3 className="font-display font-bold text-xl">Non fa per voi se…</h3>
            </div>
            <ul className="space-y-3">
              {[
                "Cercate soldi facili o guadagni garantiti: qui si parla di lavoro vero.",
                "Vi serve uno stipendio fisso a fine mese: questo non lo è.",
                "Non volete che chi vi conosce sappia cosa fate: qui si lavora a viso scoperto.",
              ].map((t, i) => (
                <li key={i} className="flex gap-3 text-[#94A3B8] leading-relaxed text-pretty">
                  <X size={18} className="text-[#F87171] shrink-0 mt-1" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* SE NON FATE NIENTE / SE VENITE */}
      <section className="border-y border-[#1E2A44] bg-[#0D1526]" data-testid="sez-bivio">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid md:grid-cols-2 gap-4 sm:gap-6">
          <motion.div {...fadeUp} className="p-7 bg-[#141F36] border border-[#F87171]/40 rounded-md" data-testid="bivio-niente">
            <div className="flex items-center gap-2 mb-4">
              <X size={20} className="text-[#F87171]" />
              <h3 className="font-display font-bold text-xl">Se non fate niente</h3>
            </div>
            <p className="text-[#94A3B8] text-base sm:text-lg leading-relaxed text-pretty">
              Martedì sera alle 20:30 fate quello che fate sempre. Il mese prossimo il conto è lo stesso. E anche quello dopo.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="p-7 bg-[#141F36] border border-[#F59E0B]/50 rounded-md" data-testid="bivio-venite">
            <div className="flex items-center gap-2 mb-4">
              <Check size={20} className="text-[#F59E0B]" />
              <h3 className="font-display font-bold text-xl">Se venite</h3>
            </div>
            <p className="text-white text-base sm:text-lg leading-relaxed text-pretty">
              Un'ora davanti a uno schermo, in due. Uscite sapendo cos'è, cosa serve e se fa per voi. Decidete voi, da informati.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FORM */}
      <section ref={formRef} id="iscriviti" className="bg-[#0B1120] scroll-mt-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <motion.div {...fadeUp}>
            <Kicker>Prenotate il vostro posto</Kicker>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl leading-tight text-balance">
              Ci vediamo <span className="text-[#F59E0B]">{DATA_LUNGA}</span>.
            </h2>
            <p className="mt-5 text-[#94A3B8] text-base sm:text-lg leading-relaxed max-w-lg text-pretty">
              Il workshop è gratuito e in diretta online su Zoom.
              <br />
              I posti sono limitati: lasciate i vostri dati e vi inviamo il link per partecipare.
            </p>
            <div className="mt-8">
              <Countdown compact prefix="form-" />
            </div>
            <div className="mt-8 flex flex-wrap gap-2.5">
              <MetaChip icon={Monitor}>Online su Zoom</MetaChip>
              <MetaChip icon={Ticket}>Gratis</MetaChip>
              <MetaChip icon={Users}>Posti limitati</MetaChip>
            </div>

            <div className="mt-8 flex items-start gap-4 p-5 bg-[#0D1526] border border-[#F59E0B]/40 rounded-md" data-testid="bonus-callout">
              <Gauge size={26} className="text-[#F59E0B] shrink-0 mt-0.5" />
              <div>
                <p className="font-display font-bold text-white">In regalo appena vi prenotate: il Semaforo</p>
                <p className="text-sm text-[#94A3B8] mt-1 text-pretty">
                  Cinque domande, un minuto: scoprite <span className="text-white font-semibold">di che colore è il vostro fine mese</span> e
                  quanti mesi di ossigeno avete, con il vostro Piano Ossigeno in PDF. Lo fate subito dopo la prenotazione, e martedì arrivate già sapendo dove siete.
                </p>
              </div>
            </div>

            <p className="mt-8 font-display font-bold text-xl sm:text-2xl text-white text-balance" data-testid="frase-chiusura">
              La serata è gratis. <span className="text-[#F59E0B]">Un altro anno così, no.</span>
            </p>
          </motion.div>

          <motion.div
            ref={bottomFormRef}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#141F36] border border-[#3A4763] rounded-xl p-6 sm:p-8 scroll-mt-20"
          >
            <LeadFormFields form={form} set={set} submit={submit} loading={loading} />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#1E2A44] bg-[#0D1526]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <motion.div {...fadeUp}><Kicker>Domande frequenti</Kicker></motion.div>
          <div className="space-y-4">
            {[
              { q: "Quanto costa?", a: "Niente. Il workshop è completamente gratuito, basta prenotare il posto." },
              { q: "Alla fine c'è una proposta?", a: "Sì. Vi dico come si lavora con me e cosa serve per iniziare. Lo scrivo qui così lo sapete prima: decidete voi, da informati." },
              { q: "E se martedì non possiamo?", a: "Il workshop è in diretta e non viene registrato. Prenotate comunque: vi avviso io della prossima data." },
              { q: "Possiamo partecipare in coppia?", a: "Certo, anzi: è pensato proprio per le coppie. Prenotate e seguitelo insieme." },
              { q: "Serve esperienza o preparazione?", a: "No. Vi bastano circa un'ora del vostro tempo e la voglia di ascoltare. Se fate il Semaforo prima, arrivate con un numero in più su di voi." },
              { q: "Dove si svolge?", a: "Tutto online su Zoom, in diretta. Vi basta un telefono o un computer con una connessione a internet." },
            ].map((f, i) => (
              <Card key={i} delay={i * 0.05} testid={`faq-${i}`}>
                <h3 className="font-display font-bold text-lg mb-2">{f.q}</h3>
                <p className="text-[#94A3B8] leading-relaxed text-pretty">{f.a}</p>
              </Card>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-12 text-center">
            <button
              data-testid="faq-cta"
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] text-[#0B1120] font-bold text-base px-7 py-4 rounded-full transition-colors"
            >
              Prenotate il vostro posto gratis <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#1E2A44] bg-[#0B1120]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
              <span className="font-display font-extrabold tracking-[0.16em] text-sm uppercase">Massimiliano Pulla</span>
            </div>
            <a href={`${BACKEND_URL}/api/privacy`} target="_blank" rel="noreferrer" className="text-sm text-[#94A3B8] hover:text-white transition-colors" data-testid="footer-privacy">
              Informativa privacy
            </a>
          </div>
          <p className="mt-6 text-xs text-[#475569] leading-relaxed max-w-3xl text-pretty">
            © 2026 Massimiliano Pulla · Tutti i diritti riservati. Il workshop presenta un'attività di network marketing
            (vendita diretta e costruzione di una squadra). I risultati e le esperienze raccontate sono personali e non
            costituiscono una promessa o garanzia di guadagno. Questo sito non è affiliato, associato o sponsorizzato da
            Instagram o Meta Platforms, Inc.
          </p>
        </div>
      </footer>

      {showStickyBar && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 bg-[#0B1120]/95 backdrop-blur-md border-t border-[#1E2A44]" data-testid="sticky-cta-bar">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-6">
            <p className="hidden lg:block text-sm text-[#94A3B8]">
              <span className="text-white font-semibold">Workshop gratuito · Network marketing online</span> — martedì 27 ottobre, ore 20:30, su Zoom.
            </p>
            <button
              data-testid="sticky-cta-button"
              onClick={scrollToBottomForm}
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] text-[#0B1120] font-bold text-base px-6 py-3.5 rounded-full transition-colors"
            >
              Prenota il posto gratis · Mar 27, 20:30 <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
